import React, {useMemo, useState} from 'react';
import {useHistory, useLocation} from 'react-router-dom';

import {Button} from '../../../../../../Button';
import {TextField} from '../../../../../../Form/TextField';
import {Icon} from '../../../../../../Icon';
import {useSaveCurrentUrl, useWeaveflowRouteContext} from '../../../context';
import {DatasetPreviewGrid} from './DatasetPreviewGrid';
import {
  buildScorerRefMap,
  buildScoreTree,
  filterScoreTree,
} from './evaluationUtils';
import {navigateToUrlPreservingPeek} from './navigationUtils';
import {ScoreTreeView} from './ScoreTreeView';
import {useEvalTabData} from './useEvalTabData';

export const ResultsSection: React.FC = () => {
  const history = useHistory();
  const location = useLocation();
  const {baseRouter} = useWeaveflowRouteContext();
  const saveCurrentUrl = useSaveCurrentUrl();
  const {call, evaluationDefinition} = useEvalTabData();
  const [filterText, setFilterText] = useState('');
  const [isSearchVisible, setIsSearchVisible] = useState(false);

  const {callOutput, evaluationObjectData} = evaluationDefinition;

  const scorerRefMap = useMemo(() => {
    const scorers = evaluationObjectData?.scorers || [];
    return buildScorerRefMap(scorers);
  }, [evaluationObjectData]);

  const scoreTree = useMemo(() => buildScoreTree(callOutput), [callOutput]);

  const filteredScoreTree = useMemo(() => {
    return filterScoreTree(scoreTree, filterText);
  }, [scoreTree, filterText]);

  const hasScores = useMemo(
    () => Object.keys(scoreTree).length > 0,
    [scoreTree]
  );
  const hasFilteredScores = useMemo(
    () => Object.keys(filteredScoreTree).length > 0,
    [filteredScoreTree]
  );

  const viewResultsUrl = useMemo(
    () =>
      baseRouter.compareEvaluationsUri(
        call.entity,
        call.project,
        [call.callId],
        null,
        null,
        'results'
      ),
    [baseRouter, call.entity, call.callId, call.project]
  );

  const viewTracesUrl = useMemo(() => {
    return baseRouter.callsUIUrl(call.entity, call.project, {
      parentId: call.callId,
    });
  }, [baseRouter, call.entity, call.project, call.callId]);

  const handleViewTracesClick = () => {
    navigateToUrlPreservingPeek(history, location, viewTracesUrl);
  };

  const handleViewResultsClick = () => {
    saveCurrentUrl();
    history.push(viewResultsUrl);
  };

  return (
    <div className="mt-16 rounded-lg px-4 shadow-sm">
      <div className="px-4 space-y-16">
        <div>
          {hasScores ? (
            <>
              <div className="mb-8 flex items-center justify-between">
                <div className="text-[16px] font-semibold">Scores</div>
                <Button
                  variant="secondary"
                  icon="search"
                  active={isSearchVisible}
                  onClick={() => setIsSearchVisible(!isSearchVisible)}
                  size="small"
                  tooltip="Toggle search"
                />
              </div>
              {isSearchVisible && (
                <div className="mb-8">
                  <TextField
                    value={filterText}
                    onChange={setFilterText}
                    placeholder="Filter scores (regex)..."
                    icon="search"
                    autoFocus
                    extraActions={
                      filterText ? (
                        <button
                          onClick={() => setFilterText('')}
                          className="absolute right-8 flex h-full items-center text-moon-500 transition-colors hover:text-moon-800"
                          aria-label="Clear filter">
                          <Icon name="close" className="h-16 w-16" />
                        </button>
                      ) : null
                    }
                  />
                </div>
              )}
              <div className="max-h-[480px] overflow-y-auto rounded-lg border border-moon-250 bg-moon-50 py-2 px-4">
                {hasFilteredScores ? (
                  <ScoreTreeView
                    tree={filteredScoreTree}
                    path={[]}
                    scorerRefMap={scorerRefMap}
                  />
                ) : (
                  <div className="py-16 text-center text-sm italic text-moon-500">
                    {filterText
                      ? 'No scores match the current filter.'
                      : 'No scores available.'}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="rounded-lg border border-dashed border-moon-200 bg-white py-16 text-center text-sm text-moon-500">
              No scores were logged for this evaluation.
            </div>
          )}
        </div>

        <div>
          <div className="mb-8 flex items-center justify-between">
            <div className="text-[16px] font-semibold">Results</div>
            <div className="flex items-center gap-8">
              <Button
                variant="secondary"
                size="small"
                className="w-[130px]"
                onClick={handleViewTracesClick}
                icon="layout-tabs"
                tooltip="View traces">
                View traces
              </Button>
              <Button
                variant="primary"
                size="small"
                className="w-[130px]"
                onClick={handleViewResultsClick}
                endIcon="forward-next"
                tooltip="View results">
                View results
              </Button>
            </div>
          </div>
          <div className="mt-4 rounded-lg border border-moon-250 bg-white overflow-hidden">
            <DatasetPreviewGrid
              evaluationEntity={call.entity}
              evaluationProject={call.project}
              evaluationCallId={call.callId}
              pageSize={10}
            />
          </div>
        </div>
      </div>
    </div>
  );
};
